import {Person} from "common/types/carriable";
import {Location} from "common/types/location";
import * as Phase2State from "./state";
import {Actions, ActionType, DEFAULT_STATE, State} from "./state";
import {CargoFormData, DepDestListItem, GroupData, Phase2EntityFormData, Phase2ScheduleNode} from "./types";
import {Route} from "antd/lib/breadcrumb/Breadcrumb";
import {useApolloClient, useMutation, useQuery} from "react-apollo";
import React, {
    MutableRefObject,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useCallback,
    Dispatch
} from "react";
import {OrgDataContext} from "../../../../context/orgData";
import {Mutations, Queries} from "./graphql";
import * as NotesForPilotRendererModule from "../../../../components/flights/scheduling/notes-for-pilot/quickedit";
import * as Util from "./util";
import {createSaveMutationVars, deRefLocations, findLegIdx, toScheduleNode} from "./util";
import {ResolvePersonIdsQuery} from "../../../../Queries/Person";
import checkScheduledPax from "../../../../components/scheduling/checkScheduledPax";
import {cleanGraphQLErrorMsg} from "../../../../common/util";
import {message, Modal} from "antd";
import {NetworkStatus} from "apollo-client";
import {personToPassenger} from "../../../../common/person";
import {cargoFormToCargoNode} from "./cargo-util";
import {Phase2SchedulerPageProps} from "./phase-2-scheduler-page";
import * as NotesForPilot from '../../../../components/flights/scheduling/notes-for-pilot';
import {ScheduledCgoNode, ScheduledPaxNode, ScheduleNode} from "../../../../components/scheduling/types";
import {getSNodeID} from "../../../../components/scheduling/util";
import * as PurgeEntitiesFromFlightTool from "./purge-entities-from-flight-tool/state";
import * as ScheduledToExpectedPaxCgo from './scheduled-to-expected-paxcgo/state';
import {useExtendedReducer} from "hooks/custom-reducer-hook";
import useBulkComplianceChecker from "../../../../hooks/compliance/bulk-compliance-checker";
import {usePaxNotifierDrawerState} from "../../../../components/editor/pax-notifier/drawer/drawer";
import useManifestPrintout from "../../../../components/scheduling/Printout/useManifestPrintout";
import {AuthContext} from "../../../../context/auth";
import {PrintoutColumn} from "./printout-modal";

/**
 * Getters and setters for modifying and reading the state of the Phase2 page.
 * Abstracts access to the actual state object so that child components consuming the state don't break from
 * structure changes.
 *
 * TODO:  Investigate ways to improve performance when using a "global" state like this.
 *        Something as simple as adding a single note to notesForPilot requires an entire re-render of the page.
 *        Look at potentially using the Redux library for this, or try to implement memoization.
 */
export interface Phase2Api {
    getState: () => Readonly<State>,
    getDispatch: () => Dispatch<Actions>
    getScheduledPaxNodes: () => ScheduledPaxNode[],
    getScheduledCgoNodes: () => ScheduledCgoNode[],
    groupData: GroupData,
    toScheduleNode: (phase2ScheduleNode: Phase2ScheduleNode) => ScheduleNode,
    data: {
        modifiedEntities: {
            get: () => State['data']['modifiedEntities'],
            getNotOnCarrier: () => State['data']['modifiedEntities'],
            set: (entities: Phase2ScheduleNode[]) => void,
            add: (entities: Phase2ScheduleNode[]) => void,
            remove: (entityIds: string[]) => void
        }
        originalEntities: {
            get: () => State['data']['originalEntities']
        }
        getModifiedEntityById: (entityId: string) => Phase2ScheduleNode | null,
        modifyEntity: (entityId: string, changedFields: Omit<Partial<Phase2ScheduleNode>, '_id'>) => void,
        removeEntities: (entityIds: string[]) => void,
        removeLeg: (departureID: string, destinationID: string) => void,
        swapEntities: (startIdx: number, endIdx: number) => void,
        swapLegs: (start: DepDestListItem, end: DepDestListItem | 'up' | 'down') => void,
        getLegs: () => DepDestListItem[],
        setLegs: (legs: DepDestListItem[]) => void,
        setEntityLegs: (entities: string[], departure: Location, destination: Location) => void,
        reset: () => void,
        refetch: () => Promise<any>,
        getNotesForPilot: () => Record<string, Phase2ScheduleNode[]>
    },
    queryInfo: {
        loading: boolean,
        error: any,
        hasError: boolean
    },
    saveInfo: {
        saving: boolean,
        hasError: boolean
    },
    saveStatus: {
        get: () => State['saveStatus']
        dismiss: () => void
    }
    schedule: {
        save: () => void,
        notifySetCreated: (setID: string) => void,
        deleteSchedule: () => void,
        cancelEdits: () => void,

        // User is done with this schedule.
        // It's up to the parent component of the Phase2SchedulerPage what this does.
        // In the current case, it returns the user to the listing/calendar page.
        finish: () => void
    },
    selection: {
        entityIds: {
            get: () => string[],
            set: (newEntities: string[]) => void,
            add: (newEntities: string[]) => void,
            toggle: (entityID: string) => void,
            remove: (removedEntities: string[]) => void,
            clear: () => void,
            all: () => void
        },
        personnelIds: {
            get: () => State['carriableSelector']['selectedPersonnelIds'],
            set: (ids: string[]) => void,
            clear: () => void,
            getAsObjects: () => {
                loading: boolean,
                objects: Person[]
            }
            addToSchedule: (departureID: string, destinationID: string) => Promise<void>
        }
    },
    mainHeader: {
        routes: Route[]
    },
    bulkComplianceChecker: ReturnType<typeof useBulkComplianceChecker>
    editMode: {
        toggle: () => void,
        set: (editing: boolean) => void,
        get: () => boolean
    }
    carriableSelector: {
        tabs: {
            set: (
                upperTab: typeof DEFAULT_STATE.carriableSelector.upperTab,
                lowerTab: typeof DEFAULT_STATE.carriableSelector.lowerTab
            ) => void,
            get: () => {
                upperTab: State['carriableSelector']['upperTab'],
                lowerTab: State['carriableSelector']['lowerTab']
            }
        },
        legSelector: {
            set: (departure: { key: string, label: string }, destination: { key: string, label: string }) => void,
            get: () => State['carriableSelector']['legSelector']
        },
        hideCarriablePanel: {
            get: () => State['carriableSelector']['hidePanel']
            set: (hide: boolean) => void,
            toggle: () => void
        }
    },
    forms: {
        entityForm: {
            get: (entityId: string) => Phase2EntityFormData,
            set: (entityId: string, formData: Phase2EntityFormData) => void,
            listEntityIds: () => string[],
            ref: MutableRefObject<any>
        },
        cargoForm: {
            get: () => State['forms']['cargoForm'],
            setFields: (fields: State['forms']['cargoForm']) => void,
            resetFields: () => void,
            submit: (departureID: string, destinationID: string) => Promise<void>,
            ref: MutableRefObject<any>
        },
        groupForm: {
            get: () => State['forms']['groupForm'],
            setFields: (fields: State['forms']['groupForm']) => void,
            resetFields: () => void,
            ref: MutableRefObject<any>
        },
        newLegForm: {
            get: () => State['forms']['newLegForm'],
            setFields: (fields: State['forms']['newLegForm']) => void,
            resetFields: () => void
        },
        searchForm: {
            set: (searchForm: typeof DEFAULT_STATE.carriableSelector.searchForm) => void,
            get: () => State['carriableSelector']['searchForm']
        }
    },
    modal: {
        paxModal: {
            open: (pax: ScheduledPaxNode) => void,
            close: () => void,
            get: () => State['modal']['paxModal']
        },
        newLegFormModal: {
            open: (
                departure?: Location | null,
                destination?: Location | null,
                callback?: State['modal']['newLegFormModal']['callback']
            ) => void,
            close: () => void,
            get: () => State['modal']['newLegFormModal'],
            callback?: State['modal']['newLegFormModal']['callback']
        }
    },
    chargeCodes: {
        getDefault: () => State['editor']['defaultChargeCode'],
        clear: () => void,
        setDefault: (chargeCode: string) => void
    },
    editor: {
        visibleLegs: {
            get: () => ({ departureID: string, destinationID: string }[]),
            isLegVisible: (departureID: string, destinationID: string) => boolean,
            showAll: () => void,
            hideAll: () => void,
            getAsStrings: () => string[],
            setAsStrings: (keys: string[]) => void,
            show: (departure: string, destination: string) => void
        }
    },
    notesForPilot: {
        getModifiedText: () => string,
        getQuickEditProps: (entityId: string) => Partial<NotesForPilot.QuickEdit.QuickEditProps>,
        editor: {
            isOpen: () => boolean,
            open: () => void,
            close: () => void,
            getNotes: () => NotesForPilot.Editor.State['notes'],
            hasNotes: () => boolean,
            submit: () => void,
            getEditorProps: () => Partial<NotesForPilot.Editor.EditorProps>
            getToolbarProps: () => NotesForPilot.Editor.ToolbarProps
        }
    },
    purgeEntitiesFromFlightTool: PurgeEntitiesFromFlightTool.Api,
    scheduledToExpectedPaxCgo: ScheduledToExpectedPaxCgo.Api,
    paxNotifierDrawer: ReturnType<typeof usePaxNotifierDrawerState>,
    printout: {
        runPrintout: (passengerColumns: PrintoutColumn[], cargoColumns: PrintoutColumn[]) => Promise<void>,
        loading: boolean
    }
}

export interface GetModifiedEntitiesOptions {
    removeOnCarrier: boolean
}

const POLL_INTERVAL = 10000;

/**
 * Implements the Phase2Api as a React hook
 */
export function usePhase2SchedulerApi(props: Phase2SchedulerPageProps){
    const apolloClient = useApolloClient();

    // Hooks -----------------------------------------------------------------------------------------------------------
    const [ state, dispatch ] = useExtendedReducer(Phase2State.Reducer, Phase2State.DEFAULT_STATE);

    const orgData = useContext(OrgDataContext);

    const groupFormRef = useRef(null);
    const cargoFormRef = useRef(null);
    const entityFormRef = useRef(null);

    // Tracks whether INIT_DATA has been dispatched at least once for the current schedule identity.
    // Allows INIT_DATA to fire on first load even in edit mode (e.g. /schedule/new),
    // but blocks subsequent fires during edit mode to prevent clobbering user edits.
    // Reset when the schedule identity changes so a reused component instance re-initializes.
    const dataInitializedRef = useRef(false);
    const prevGroupKeyRef = useRef<string | null>(null);
    // -----------------------------------------------------------------------------------------------------------------

    // External UI state -----------------------------------------------------------------------------------------------
    const paxNotifierDrawerState = usePaxNotifierDrawerState();
    // -----------------------------------------------------------------------------------------------------------------

    // Printout --------------------------------------------------------------------------------------------------------
    const { user: cognitoUser } = useContext(AuthContext);
    const [fetchSchedulePrintout, schedulePrintoutFetching] = useManifestPrintout('ScheduleWithLegs', 'Default');
    // -----------------------------------------------------------------------------------------------------------------

    function _toScheduleNode(phase2ScheduleNode: Phase2ScheduleNode){
        return toScheduleNode(phase2ScheduleNode, state.formDataMap);
    }

    // Read schedule ---------------------------------------------------------------------------------------------------
    let queryVars = null;
    if (props.groupData){
        queryVars = {
            tpID: props.groupData.tpID,
            customerID: props.groupData.customerID,
            scheduledFlightDate: props.groupData.date,
            scheduledGroup: props.groupData.name,
            lastKnownController: props.groupData.lastKnownController
        }
    }

    // Reset the dataInitialized flag when the schedule identity changes (e.g. React Router
    // reusing the same component instance across /edit → /view route transitions).
    const groupKey = queryVars
        ? `${queryVars.scheduledFlightDate}::${queryVars.scheduledGroup}::${queryVars.lastKnownController}`
        : null;
    if (groupKey !== prevGroupKeyRef.current) {
        prevGroupKeyRef.current = groupKey;
        dataInitializedRef.current = false;
    }
    const queryResult = useQuery(Queries.GetScheduledPaxCgoV2, {
        variables: queryVars,
        skip: !queryVars,
        // cache-and-network: immediately shows whatever is in the Apollo cache (e.g. the result
        // written by writeQuery after a save) while simultaneously fetching fresh data from the
        // network. This eliminates the "old data flash" by pre-populating with the saved result
        // and then reconciling with the authoritative server response.
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all'
    })
    // -----------------------------------------------------------------------------------------------------------------

    // Compliance checker hook ------------------------------------------------------------------------------------------------
    function onComplianceCheckerFinished(){
        queryResult.refetch();
    }

    const bulkComplianceChecker = useBulkComplianceChecker({
        onCompleted: onComplianceCheckerFinished
    });
    // -----------------------------------------------------------------------------------------------------------------

    // Write schedule --------------------------------------------------------------------------------------------------
    const [ mutateSchedule, mutationResult ] = useMutation(Mutations.SaveSchedulePaxCgoV2, {
        onCompleted: (data) => {
            const paxnodes = data?.SchedulePaxCgo_V2?.paxnodes as ScheduledPaxNode[] || [];
            const paxIds = paxnodes.map(pax => pax._id);
            bulkComplianceChecker.begin(paxIds);
        }
    });
    // -----------------------------------------------------------------------------------------------------------------

    // Updates the state as a side effect to prop changes --------------------------------------------------------------

    // 1. Sync editMode from props into local state
    useEffect(() => {
        if (props.editMode !== undefined && state.editMode !== props.editMode){
            dispatch({ type: ActionType.SET_EDIT_MODE, payload: props.editMode });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.editMode]);

    // 2. Polling control — start polling in view mode, stop in edit mode
    useEffect(() => {
        if (!state.editMode){
            queryResult.startPolling(POLL_INTERVAL);
        } else {
            queryResult.stopPolling();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.editMode]);

    // 3. INIT_DATA — populate local state from server query result.
    //    Fires whenever the Apollo query data changes while not in edit mode.
    //    Exception: fires once on first load even in edit mode (e.g. /schedule/new route).
    //    dataInitializedRef resets whenever the schedule identity (date/group/lkc) changes,
    //    which handles the case where React Router reuses the same component instance across
    //    the /edit → /view route transition.
    useEffect(() => {
        if (!queryResult.data?.GetScheduledPaxCgoV2) return;

        const isFirstLoad = !dataInitializedRef.current;
        const canInit = isFirstLoad || !state.editMode;

        if (!canInit) return;

        const data = queryResult.data.GetScheduledPaxCgoV2;
        const entities = [...data.paxnodes, ...data.cgonodes];
        dispatch({
            type: ActionType.INIT_DATA,
            entities,
            group: {
                ...props.groupData,
                lastKnownControllerObj: queryResult.data.getLastKnownController as Location
            }
        });
        dataInitializedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryResult.data?.GetScheduledPaxCgoV2, state.editMode]);

    // 4. Sync query loading/error status into state.
    //    Only show loading spinner for initial load (networkStatus 1) or variable changes (2).
    //    Background polls (6) and compliance refetches (4) must NOT trigger the spinner.
    useEffect(() => {
        const isInitialLoad = queryResult.networkStatus === NetworkStatus.loading ||
            queryResult.networkStatus === NetworkStatus.setVariables;
        // Show spinner only for initial loads, not polls or refetches
        const effectiveLoading = queryResult.loading && isInitialLoad;

        if (
            effectiveLoading !== state.queryStatus.loading ||
            queryResult.error !== state.queryStatus.error
        ){
            dispatch({ type: ActionType.SET_QUERY_STATUS, payload: { loading: effectiveLoading, error: queryResult.error } });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryResult.networkStatus, queryResult.loading, queryResult.error]);

    // 5. Show/hide the carriable (left) panel when toggling edit mode
    useEffect(() => {
        if (state.editMode){
            dispatch({ type: ActionType.SET_HIDE_CARRIABLE_PANEL, payload: false });
        } else {
            dispatch({ type: ActionType.SET_HIDE_CARRIABLE_PANEL, payload: true });
        }
    }, [state.editMode]);

    // 6. When the default charge code changes, update charge code field error states.
    //    - If a default is now set: clear errors on all blank charge code fields via setFields()
    //      (the documented API), so they no longer block saving.
    //    - If the default was cleared: re-run validation so blank fields show errors again.
    //    Avoids { force: true } which is an undocumented rc-form internal.
    useEffect(() => {
        const form = entityFormRef.current?.props?.form ?? entityFormRef.current;
        if (!form) return;

        const defaultChargeCode = state.editor.defaultChargeCode;

        if (defaultChargeCode) {
            // Build a setFields patch that clears errors for every blank charge code field.
            const fieldUpdates: Record<string, { value: any, errors: null }> = {};
            Object.keys(state.formDataMap).forEach((entityId) => {
                const fieldKey = `${entityId}::chargeCode`;
                const currentValue = form.getFieldValue(fieldKey);
                if (!currentValue) {
                    fieldUpdates[fieldKey] = { value: currentValue, errors: null };
                }
            });
            if (Object.keys(fieldUpdates).length) {
                form.setFields(fieldUpdates);
            }
        } else {
            // Default was cleared — re-validate so blank fields show required errors.
            // validateFields without force only runs on touched/errored fields, which is
            // sufficient here since we just need to re-flag the blank ones.
            form.validateFields();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.editor.defaultChargeCode]);

    // -----------------------------------------------------------------------------------------------------------------

    // Tune visible legs -----------------------------------------------------------------------------------------------
    // Visible legs will be determined by how many legs there are. If there are more than 5 legs, all legs will be
    // collapsed by default.
    useEffect(() => {
        if (state.data.originalEntities.length > 0){
            // Data has just been filled in after query finished.
            const legs = Util.DepDestList.build(state.data.originalEntities);

            if (legs.length > 5){
                dispatch({ type: ActionType.SET_VISIBLE_LEGS, mode: 'objects', action: 'set', payload: [] });
            }
            else
            {
                const depDestList = legs.map(item => ({
                    departureID: item.departure._id,
                    destinationID: item.destination._id
                }));
                dispatch({ type: ActionType.SET_VISIBLE_LEGS, mode: 'objects', action: 'set', payload: depDestList });
            }
        }
    }, [
        state.data.originalEntities.length > 0
    ]);
    // -----------------------------------------------------------------------------------------------------------------

    // Dereferences person id list to real personnel documents ---------------------------------------------------------
    const derefSelPersonnelResult = useQuery(ResolvePersonIdsQuery, {
        variables: {
            ids: state.carriableSelector.selectedPersonnelIds
        },
        fetchPolicy: 'cache-first',
        errorPolicy: 'all'
    })
    const selectedPersonnelObjs = (derefSelPersonnelResult.data?.resolve_entity_ids || []) as (Person | undefined)[];
    // -----------------------------------------------------------------------------------------------------------------

    function createDispatchFunc<T extends ActionType>(type: T){
        return (payload: any) => dispatch({ type: type as any, payload: payload });
    }

    function createDispatchFuncNoPayload<T extends ActionType>(type: T){
        return () => dispatch({ type: type as any });
    }

    function setEditMode(editing: boolean){
        if (props.editMode !== undefined && props.onEditModeChange){
            props.onEditModeChange(editing);
            return;
        }
        dispatch({ type: ActionType.SET_EDIT_MODE, payload: editing });
    }

    let groupData: GroupData = null;
    if (queryResult.data?.getLastKnownController){
        groupData = {
            ...props.groupData,
            lastKnownControllerObj: queryResult.data.getLastKnownController
        }
    }

    // If I don't use useMemo, the filtered list will return a different reference
    // every render. This ensures the filtered version of modifiedEntities only changes references
    // when modifiedEntities changes.
    const modifiedEntitiesNotOnCarrier = useMemo(() => {
        return state.data.modifiedEntities.filter((entity) => {
            return !Boolean(entity.currentCarrierID)
        })
    }, [ state.data.modifiedEntities ])

    // Build printout callback -----------------------------------------------------------------------------------------
    const runPrintout = useCallback((passengerColumns: PrintoutColumn[], cargoColumns: PrintoutColumn[]): Promise<void> => {
        const entities = state.data.modifiedEntities;
        const passengers: any[] = [];
        const cargo: any[] = [];

        entities.forEach((entity) => {
            if (entity.classType === 'flytsuite.paxnode') {
                const pax = entity as any;
                passengers.push({
                    lastName: pax.lastName,
                    firstName: pax.firstName,
                    employer: pax.employerID && pax.employerID.name,
                    chargeCode: pax.chargeCode && (pax.chargeCode as string).toUpperCase(),
                    paxWeight: pax.paxWeight,
                    bagWeight: pax.bagWeight,
                    departure: pax.departureID && pax.departureID.name,
                    destination: pax.destinationID && pax.destinationID.name,
                    dob: pax.personID && pax.personID.dob,
                    nation: pax.personID && pax.personID.nation,
                    credentials: pax.personID && pax.personID.credentials
                });
            } else {
                const cgo = entity as any;
                cargo.push({
                    name: cgo.name,
                    weight: cgo.weight,
                    departure: cgo.departureID && cgo.departureID.name,
                    destination: cgo.destinationID && cgo.destinationID.name,
                    attentionTo: cgo.attentionTo,
                    deliveredBy: cgo.deliveredBy
                });
            }
        });

        const userAttr = cognitoUser?.attributes || {};
        const body = {
            issuedBy: `${userAttr.given_name || ''} ${userAttr.family_name || ''}`.trim(),
            orgName: orgData?.customer?.name,
            scheduledFlightDate: groupData?.date,
            scheduledGroup: groupData?.name,
            lastKnownControllerName: groupData?.lastKnownControllerObj?.name,
            passengers,
            cargo,
            passengerColumns,
            cargoColumns
        };

        console.debug('fetchSchedulePrintout body', body);

        return (fetchSchedulePrintout as any)(body, true, (groupData?.name || 'Schedule') + ' Schedule Manifest.xlsx');
    }, [state.data.modifiedEntities, groupData, orgData, cognitoUser, fetchSchedulePrintout]);
    // -----------------------------------------------------------------------------------------------------------------

    // Implements the API used by child components to communicate with other child components --------------------------
    const api: Phase2Api = {
        getState: () => state,
        getDispatch: () => dispatch,
        getScheduledPaxNodes: () => state.data.modifiedEntities.filter(e => e.classType === 'flytsuite.paxnode') as ScheduledPaxNode[],
        getScheduledCgoNodes: () => state.data.modifiedEntities.filter(e => e.classType === 'flytsuite.cgonode') as ScheduledCgoNode[],
        bulkComplianceChecker: bulkComplianceChecker,
        groupData: groupData,
        toScheduleNode: _toScheduleNode,
        data: {
            modifiedEntities: {
                get: () => state.data.modifiedEntities,
                getNotOnCarrier: () => modifiedEntitiesNotOnCarrier,
                set: (entities) => dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, set: entities }),
                add: (entities) => dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, add: entities }),
                remove: (entityIds) => dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, remove: entityIds }),
            },
            originalEntities: {
                get: () => state.data.originalEntities
            },
            getModifiedEntityById: (entityId) => state.data.modifiedEntities
                .find((entity) => getSNodeID(entity as ScheduleNode) === entityId),
            removeLeg(departureID, destinationID) {
                const entitiesToRemove = state.data.modifiedEntities
                    .filter(entity => (
                        entity.departureID?._id === departureID && entity.destinationID?._id === destinationID
                    ))
                    .map(e => getSNodeID(_toScheduleNode(e)));

                if (entitiesToRemove.length){
                    dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, remove: entitiesToRemove });
                }
            },
            modifyEntity(entityId, changedFields){
                const entityObj = state.data.modifiedEntities
                    .find(e => getSNodeID(_toScheduleNode(e)) === entityId);

                if (!entityObj) return;

                const newEntity = {
                    ...entityObj,
                    ...changedFields
                } as Phase2ScheduleNode;

                dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, add: [newEntity] });
            },

            removeEntities(entities) {
                dispatch({type: ActionType.SET_MODIFIED_ENTITIES, remove: entities})
            },

            swapEntities(startIdx, endIdx) {
                if (!state.data.modifiedEntities.length) return;
                if (startIdx < 0 || startIdx >= state.data.modifiedEntities.length) return;
                if (endIdx < 0 || endIdx >= state.data.modifiedEntities.length) return;

                const entityListCopy = [...state.data.modifiedEntities];

                const startObj = entityListCopy[startIdx];
                const endObj = entityListCopy[endIdx];

                if (
                    startObj.departureID?._id !== endObj.departureID?._id ||
                    startObj.destinationID?._id !== endObj.destinationID?._id
                ){
                    // Prevent reordering between legs
                    return;
                }

                entityListCopy[startIdx] = endObj;
                entityListCopy[endIdx] = startObj;

                dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, set: entityListCopy });
            },

            swapLegs(start, arg1) {

                // Compute legs from data
                const legs = this.getLegs();

                const startIdx = findLegIdx(legs, start);
                let endIdx = -1;

                if (typeof arg1 === 'string'){
                    // arg1 is either "up" or "down"
                    if (arg1 === 'up'){
                        endIdx = startIdx - 1;
                    }
                    else if (arg1 === 'down')
                    {
                        endIdx = startIdx + 1;
                    }
                }
                else
                {
                    // arg1 is another DepDestListItem
                    endIdx = findLegIdx(legs, arg1);
                }

                if (
                    startIdx < 0 || endIdx < 0 ||
                    startIdx >= legs.length || endIdx >= legs.length
                ){
                    console.error(new Error(`Index out of bounds: startIdx: ${startIdx}, endIdx: ${endIdx}`))
                    return;
                }

                // Swap
                {
                    const temp = legs[startIdx];
                    legs[startIdx] = legs[endIdx];
                    legs[endIdx] = temp;
                }

                this.setLegs(legs);
            },

            getLegs() {return Util.DepDestList.build(api.data.modifiedEntities.get()) },

            setLegs(legs) {
                const newEntities = Util.DepDestList.toEntities(legs);
                dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, set: newEntities });
            },

            setEntityLegs(entityIds, departure, destination) {
                const newEntities = entityIds
                    .map(entityId => state.data.modifiedEntities.find(e => getSNodeID(_toScheduleNode(e)) === entityId))
                    .filter(e => e)
                    .map((entity) => {
                        return {
                            ...entity,
                            departureID: departure,
                            destinationID: destination
                        }
                    });

                dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, add: newEntities });
                dispatch({ type: ActionType.SET_SELECTED_ENTITY_IDS, set: [] });
            },

            reset(){ dispatch({ type: ActionType.RESET_DATA }); },
            refetch(){
                return queryResult.refetch();
            },

            getNotesForPilot() {
                /*  Gather all notesForPilot values from pax and cargo and create a dictionary of them with this structure:
                    {
                        <MESSAGE>: ScheduleNode[]
                    }
                 */

                return Util.getNoteMap(state.data.modifiedEntities as ScheduleNode[])
            }
        },
        queryInfo: {
            loading: state.queryStatus.loading,
            error: state.queryStatus.error,
            hasError: !!state.queryStatus.error
        },
        saveInfo: {
            saving: mutationResult.loading,
            hasError: !!mutationResult.error
        },
        saveStatus: {
            get: () => state.saveStatus,
            dismiss: createDispatchFuncNoPayload(ActionType.DISMISS_SAVE_STATUS)
        },
        editMode: {
            get: () => state.editMode,
            toggle: () => setEditMode(!state.editMode),
            set: setEditMode
        },
        schedule: {
            cancelEdits: () => {
                // Reset local edits back to the pre-edit server state before navigating away.
                dispatch({ type: ActionType.RESET_DATA });
                props.onCancel?.();
            },
            notifySetCreated: (setID) => props.onNewSetCreated?.(setID),
            finish: () => props.onFinished?.(),
            save: () => {
                let passengers: any[] = state.data.modifiedEntities.filter((item) => item.classType === 'flytsuite.paxnode');
                const groupForm = groupFormRef.current?.props?.form ?? groupFormRef.current;
                const entityForm = entityFormRef.current?.props?.form ?? entityFormRef.current;

                if (!groupForm?.validateFieldsAndScroll || !entityForm?.validateFieldsAndScroll) {
                    message.error('The scheduling form is not ready. Please try again.');
                    return;
                }

                checkScheduledPax(passengers, () => {
                    groupForm.validateFieldsAndScroll((groupErr: boolean, groupValues: any) => {
                        if (!groupErr){
                            entityForm.validateFieldsAndScroll((entityErr: boolean) => {
                                if (entityErr) return;

                                // Move pax with null personID to deleted — they cannot be persisted
                                // and the user has already been warned by checkScheduledPax.
                                const nullPersonPax = passengers.filter((pax: any) => !pax.personID);
                                const nullPersonIds = nullPersonPax.map((pax: any) => getSNodeID(_toScheduleNode(pax)));
                                const entitiesToSave = nullPersonIds.length
                                    ? state.data.modifiedEntities.filter(
                                        e => !nullPersonIds.includes(getSNodeID(_toScheduleNode(e)))
                                      )
                                    : state.data.modifiedEntities;
                                const deletedEntities = [
                                    ...state.data.deletedEntities,
                                    // Only include null-personID pax that have an _id (i.e. already persisted server-side).
                                    // Temp/unsaved records use __temp_id and have no _id, so they need no delete call.
                                    ...nullPersonPax.filter((pax: any) => pax._id)
                                ];

                                let vars = createSaveMutationVars(
                                    orgData,
                                    {
                                        date: groupValues.date,
                                        lastKnownController: groupValues.lastKnownController.key,
                                        group: groupValues.name
                                    },
                                    entitiesToSave,
                                    deletedEntities,
                                    state.formDataMap,
                                    state.editor.defaultChargeCode
                                );
                                mutateSchedule({
                                    variables: vars
                                })
                                .then((mutResult) => {
                                        // Remove null-personID pax from local state now that save succeeded
                                        if (nullPersonIds.length){
                                            dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, remove: nullPersonIds });
                                        }

                                        // Commit the server-returned entities as the new canonical baseline.
                                        // Using the server response (not entitiesToSave) ensures real _id values
                                        // for newly created records are used. Falls back to entitiesToSave if
                                        // the mutation response doesn't include entities.
                                        const savedPaxnodes = mutResult?.data?.SchedulePaxCgo_V2?.paxnodes || [];
                                        const savedCgonodes = mutResult?.data?.SchedulePaxCgo_V2?.cgonodes || [];
                                        const savedEntities = [...savedPaxnodes, ...savedCgonodes];
                                        dispatch({
                                            type: ActionType.COMMIT_SAVE,
                                            entities: savedEntities.length ? savedEntities : entitiesToSave as any
                                        });

                                        // Write the mutation response directly into the Apollo cache.
                                        // The backend now returns only the surviving entities (deleted ones are
                                        // excluded server-side), so no client-side filtering is needed.
                                        // Using the server response also ensures all nested objects have correct
                                        // __typename values so Apollo cache normalization works properly.
                                        if (queryVars && savedEntities.length) {
                                            try {
                                                apolloClient.writeQuery({
                                                    query: Queries.GetScheduledPaxCgoV2,
                                                    variables: queryVars,
                                                    data: {
                                                        ...queryResult.data,
                                                        GetScheduledPaxCgoV2: {
                                                            __typename: 'ScheduledPaxCgoResult',
                                                            paxnodes: savedPaxnodes,
                                                            cgonodes: savedCgonodes
                                                        }
                                                    }
                                                });
                                            } catch (e) {
                                                console.warn('[phase2/writeQuery] Failed to write save result to Apollo cache', e);
                                            }
                                        }

                                        props.onScheduleSaved?.({
                                            variables: vars,
                                            modifiedEntities: entitiesToSave as ScheduleNode[]
                                        });
                                        dispatch(
                                            {
                                                type: ActionType.SET_SAVE_STATUS,
                                                payload: {
                                                    status: 'success',
                                                    message: `Successfully saved ${entitiesToSave.length} pax/cgo`
                                                }
                                            }
                                        );
                                    })
                                    .catch((e) => {
                                        console.error('Failed to save schedule due to error: ', e);
                                        dispatch(
                                            {
                                                type: ActionType.SET_SAVE_STATUS,
                                                payload: {
                                                    status: 'error',
                                                    message: 'Failed to save schedule',
                                                    description: cleanGraphQLErrorMsg(e)
                                                }
                                            }
                                        );
                                    })
                            })
                        }
                    })
                });
            },
            deleteSchedule: () => {
                const vars = createSaveMutationVars(
                    orgData,
                    {
                        date: props.groupData.date,
                        lastKnownController: props.groupData.lastKnownController,
                        group: props.groupData.name
                    },
                    [],
                    state.data.originalEntities,
                    state.formDataMap
                )
                mutateSchedule({
                    variables: vars,
                })
                    .then(() => {
                        message.success('Schedule deleted successfully');
                        props.onScheduleDeleted?.()
                    })
                    .catch((e) => {
                        message.error("Failed to delete schedule");
                        console.error('Failed to delete schedule: ', e);
                    })
            }
        },
        mainHeader: {
            routes: props.mainHeaderRoutes
        },
        selection: {
            entityIds: {
                get: () => state.editor.selectedEntityIds,
                set: (newEntities) => dispatch({ type: ActionType.SET_SELECTED_ENTITY_IDS, set: newEntities }),
                add: (newEntities) => dispatch({ type: ActionType.SET_SELECTED_ENTITY_IDS, add: newEntities }),
                remove: (removedEntities) => dispatch({ type: ActionType.SET_SELECTED_ENTITY_IDS, remove: removedEntities }),
                toggle(entityID){
                    if (!state.editor.selectedEntityIds.includes(entityID))
                        this.add([entityID]);
                    else
                        this.remove([entityID]);
                },
                clear: () => dispatch({ type: ActionType.SET_SELECTED_ENTITY_IDS, set: [] }),
                all: () => {
                    let entities = state.data.modifiedEntities.map(e => getSNodeID(_toScheduleNode(e)));
                    dispatch({ type: ActionType.SET_SELECTED_ENTITY_IDS, set: entities });
                }
            },
            personnelIds: {
                set: createDispatchFunc(ActionType.SET_SELECTED_PERSONNEL_IDS),
                get: () => state.carriableSelector.selectedPersonnelIds,
                getAsObjects: () => ({
                    loading: [NetworkStatus.loading, NetworkStatus.setVariables].includes(derefSelPersonnelResult.networkStatus),
                    objects: selectedPersonnelObjs
                }),
                clear: () => dispatch({ type: ActionType.SET_SELECTED_PERSONNEL_IDS, payload: [] }),
                addToSchedule: async (departureID: string, destinationID: string) => {
                    // If derefSelPersonnelResult is still loading, we can't do anything.
                    if (derefSelPersonnelResult.loading){
                        message.error('Error: Personnel data is still loading. Please try again.')
                        return;
                    }

                    // Re-reference locations to real location documents
                    const locResult = await deRefLocations(apolloClient, [departureID, destinationID]);
                    if (locResult.errors.length){
                        message.error("Failed to get locations.");
                        return;
                    }
                    const [ departure, destination ] = locResult.data;

                    // Convert personnel to PaxNode stubs
                    function toPassenger(person: Person){

                        const pax = personToPassenger(
                            person,
                            {
                                departureID: departure,
                                destinationID: destination,
                                customerID: person.customerID,
                                tpID: orgData.transporterID,
                                chargeCode: null
                            }
                        );

                        // Using _id is a bad idea
                        pax.__temp_id = Util.genTempID();
                        delete pax._id;
                        return pax;
                    }

                    let autoFilledPersons = [];

                    selectedPersonnelObjs.forEach((person) => {
                        if (person.lastPaxWeight || person.lastBagWeight || person.lastBagCount){
                            autoFilledPersons.push(person);
                        }
                    });

                    if (autoFilledPersons.length){
                        message.info(`Previous weight info found for ${autoFilledPersons.length} personnel. 
                    Auto-filled fields.`, 5);
                    }

                    const paxNodes = selectedPersonnelObjs.map(toPassenger);

                    dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, add: paxNodes })
                }
            }
        },
        carriableSelector: {
            tabs: {
                set: (upperTab, lowerTab) =>
                    dispatch({ type: ActionType.SET_CARRIABLE_SELECTOR_TAB, payload: { upperTab, lowerTab } }),
                get: () => ({
                    upperTab: state.carriableSelector.upperTab,
                    lowerTab: state.carriableSelector.lowerTab
                })
            },
            legSelector: {
                set: (departure, destination) => (
                    dispatch({ type: ActionType.SET_LEG_SELECTOR, payload: { departure, destination } })
                ),
                get: () => state.carriableSelector.legSelector
            },
            hideCarriablePanel: {
                get: () => state.carriableSelector.hidePanel,
                set: createDispatchFunc(ActionType.SET_HIDE_CARRIABLE_PANEL),
                toggle: () => dispatch({ type: ActionType.SET_HIDE_CARRIABLE_PANEL, payload: undefined }),
            }
        },
        forms: {
            entityForm: {
                get: (entityId) => {
                    return state.formDataMap[entityId];
                },
                set: (entityId, formData) => dispatch({ type: ActionType.SET_ENTITY_FORM_DATA, entityId, payload: formData }),
                listEntityIds: () => Object.keys(state.formDataMap),
                ref: entityFormRef
            },
            cargoForm: {
                get: () => state.forms.cargoForm,
                setFields: (fields) => dispatch({ type: ActionType.SET_FORM_DATA, cargo: fields }),
                resetFields: () => dispatch({
                    type: ActionType.RESET_FORM_DATA,
                    cargo: true
                }),
                submit: async (departureID, destinationID) => {

                    // Make sure form is valid
                    cargoFormRef.current.validateFieldsAndScroll(async (err: unknown, formValues: CargoFormData) => {
                        if (err) return;

                        // Re-reference locations to real location documents
                        const locResult = await deRefLocations(apolloClient, [departureID, destinationID]);
                        if (locResult.errors.length){
                            message.error("Failed to get locations.");
                            return;
                        }
                        const [ departure, destination ] = locResult.data;

                        const cgoNode = cargoFormToCargoNode(formValues, orgData, departure, destination);

                        dispatch({ type: ActionType.SET_MODIFIED_ENTITIES, add: [cgoNode] });
                    })
                },
                ref: cargoFormRef
            },
            groupForm: {
                get: () => state.forms.groupForm,
                setFields: (fields) => dispatch({ type: ActionType.SET_FORM_DATA, group: fields }),
                resetFields: () => dispatch({
                    type: ActionType.RESET_FORM_DATA,
                    group: true
                }),
                ref: groupFormRef
            },
            newLegForm: {
                get: () => state.forms.newLegForm,
                setFields: (fields) => dispatch({ type: ActionType.SET_FORM_DATA, newLeg: fields }),
                resetFields: () => dispatch({
                    type: ActionType.RESET_FORM_DATA,
                    newLeg: true
                })
            },
            searchForm: {
                get: () => state.carriableSelector.searchForm,
                set: createDispatchFunc(ActionType.SET_CARRIABLE_SEL_SEARCH)
            }
        },
        modal: {
            newLegFormModal: {
                get: () => state.modal.newLegFormModal,
                open: (departure=null, destination=null, callback=null) => {

                    if (departure && destination){
                        const newLegValue = {
                            departure: { key: departure._id, label: departure.name },
                            destination: { key: destination._id, label: destination.name },
                        }
                        dispatch({ type: ActionType.SET_NEW_LEG_MODAL, visible: true, newLegValue: newLegValue, callback });
                        return;
                    }
                    dispatch({ type: ActionType.SET_NEW_LEG_MODAL, visible: true, callback });
                },
                close: () => dispatch({ type: ActionType.SET_NEW_LEG_MODAL, visible: false }),
                callback: state.modal.newLegFormModal.callback
            },
            paxModal: {
                get: () => state.modal.paxModal,
                open: (pax) => dispatch({ type: ActionType.SET_PAXNODE_MODAL, pax, visible: true }),
                close: () => dispatch({ type: ActionType.SET_PAXNODE_MODAL, visible: false })
            },
        },
        chargeCodes: {
            getDefault: () => state.editor.defaultChargeCode,
            setDefault: (value: string) => {
                dispatch({ type: ActionType.SET_DEFAULT_CHARGE_CODE, payload: value });
            },
            clear: () => dispatch({ type: ActionType.CLEAR_CHARGE_CODES })
        },
        editor: {
            visibleLegs: {
                get: () => state.editor.visibleLegs,
                show: (departureID, destinationID) => {
                    const payload = {
                        departureID: departureID,
                        destinationID: destinationID
                    }
                    dispatch({ type: ActionType.SET_VISIBLE_LEGS, mode: 'objects', action: 'add', payload: [payload] });
                },
                isLegVisible: (departureID, destinationID) => (
                    Boolean(
                        state.editor.visibleLegs.find((item) => (
                            item.departureID === departureID && item.destinationID === destinationID)
                        )
                    )
                ),
                showAll: () => {
                    const data = Util.DepDestList.build(state.data.modifiedEntities);

                    const depDestList = data
                        .map(item => ({
                            departureID: item.departure._id,
                            destinationID: item.destination._id
                        }));

                    dispatch({ type: ActionType.SET_VISIBLE_LEGS, mode: 'objects', action: 'set', payload: depDestList })
                },
                hideAll: () => {
                    dispatch({ type: ActionType.SET_VISIBLE_LEGS, mode: 'objects', action: 'set', payload: [] });
                },
                setAsStrings: (keys) => (
                    dispatch({ type: ActionType.SET_VISIBLE_LEGS, mode: 'strings', action: 'set', splitBy: '::', payload: keys })
                ),
                getAsStrings: () => {
                    return state.editor.visibleLegs
                        .map(item => `${item.departureID}::${item.destinationID}`)
                }
            }
        },
        notesForPilot: {
            getModifiedText: () => state.notesForPilotPopup.modifiedText,
            getQuickEditProps: (entityId: string) => {

                let componentState = NotesForPilotRendererModule.DEFAULT_STATE;

                if (entityId === state.notesForPilotPopup.entityId)
                    // If this is not the current entity having notes edited, then this NotesForPilotQuickedit should be closed.
                    componentState = {
                        open: state.notesForPilotPopup.open,
                        editMode: state.notesForPilotPopup.editMode,
                        modifiedText: state.notesForPilotPopup.modifiedText,
                        originalText: state.notesForPilotPopup.originalText
                    }

                const stateProps = {
                    state: componentState,
                    dispatch: (value) => {
                        dispatch({ type: ActionType.NOTES_FOR_PILOT_POPUP, entityId, action: value })
                    }
                }

                return {
                    overrideState: stateProps
                }
            },
            editor: {
                isOpen: () => state.notesForPilotEditor.open,
                open: () => dispatch({ type: ActionType.NOTES_FOR_PILOT_EDITOR, open: true }),
                close: () => dispatch({ type: ActionType.NOTES_FOR_PILOT_EDITOR, open: false }),
                getNotes: () => state.notesForPilotEditor.state.notes,
                hasNotes: () => Boolean(Object.keys(state.notesForPilotEditor.state.notes).length),
                submit: () => {

                    function handleSubmit(){
                        dispatch({ type: ActionType.SUBMIT_NOTES_FOR_PILOT_EDITOR });
                    }

                    // Confirm with user that there are notes that are not assigned to any PAX/CGO and
                    // it will remove them when they submit.
                    const unassignedNotes = NotesForPilot.Editor.Util
                        .findUnassignedNoteIds(state.notesForPilotEditor.state);

                    if (unassignedNotes.length){
                        Modal.confirm({
                            title: 'Unassigned notes found',
                            content: (
                                `${unassignedNotes.length} notes have no PAX/CGO assigned and they will be removed if you continue.`
                            ),
                            okText: 'Continue',
                            onOk: handleSubmit
                        })
                        return;
                    }

                    handleSubmit();
                },
                getEditorProps: () => {
                    return {
                        overrideState: {
                            state: state.notesForPilotEditor.state,
                            dispatch: (action) => dispatch({ type: ActionType.NOTES_FOR_PILOT_EDITOR, action: action })
                        }
                    }
                },
                getToolbarProps: () => {
                    return {
                        state: state.notesForPilotEditor.state,
                        dispatch: (action) => dispatch({ type: ActionType.NOTES_FOR_PILOT_EDITOR, action: action })
                    }
                }
            }
        },
        purgeEntitiesFromFlightTool: null,
        scheduledToExpectedPaxCgo: null,
        paxNotifierDrawer: paxNotifierDrawerState,
        printout: {
            runPrintout,
            loading: schedulePrintoutFetching as boolean
        }
    }
    // -----------------------------------------------------------------------------------------------------------------

    api.purgeEntitiesFromFlightTool = PurgeEntitiesFromFlightTool.useApi(api);
    api.scheduledToExpectedPaxCgo = ScheduledToExpectedPaxCgo.useApi(api);

    return api;
}


























